<template>
  <div>
    <FilterModal ref="filterModal" :transformers="transformers" @saved="loadFilters" />
    <MappingRuleModal
      ref="mappingModal"
      :transformers="transformers"
      :transformTypes="transformTypes"
      :versoColumns="versoColumns"
      @saved="loadOverrides"
    />
    <DimensionModal ref="dimensionModal" @saved="loadDimensions" />

    <CTabs variant="tabs" class="nav-tabs-boxed" :active-tab="activeTab" @update:activeTab="onTabChange">

      <!-- ═══ ABA 1: MAPEAMENTO ═══ -->
      <CTab title="Mapeamento de Campos">

        <!-- LISTA de transformers -->
        <div v-if="!selectedTransformer">
          <div class="d-flex align-items-center flex-wrap mb-3" style="gap:8px">
            <input
              type="text"
              class="form-control form-control-sm"
              style="max-width:280px"
              v-model="transformerSearch"
              placeholder="Buscar consulta..."
              aria-label="Buscar consulta"
            />
            <div class="d-flex" style="gap:6px">
              <CButton
                size="sm"
                :color="filterChip === 'all' ? 'primary' : 'light'"
                @click="filterChip = 'all'"
              >Todas <CBadge :color="filterChip === 'all' ? 'light' : 'secondary'" class="ml-1">{{ chipCounts.all }}</CBadge></CButton>
              <CButton
                size="sm"
                :color="filterChip === 'mapped' ? 'primary' : 'light'"
                @click="filterChip = 'mapped'"
              >Com vínculos <CBadge :color="filterChip === 'mapped' ? 'light' : 'secondary'" class="ml-1">{{ chipCounts.mapped }}</CBadge></CButton>
            </div>
          </div>

          <CCard>
            <CCardBody class="p-0">
              <table class="table table-sm table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Consulta</th>
                    <th>Endpoint</th>
                    <th>Vínculos</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="loading">
                    <tr v-for="i in 7" :key="'sk-' + i">
                      <td colspan="4"><div class="bg-light rounded" style="height:16px;width:100%;animation:pulse 1.2s ease-in-out infinite"></div></td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr
                      v-for="t in filteredTransformers"
                      :key="t.transformer_key"
                      style="cursor:pointer"
                      @click="openTransformer(t)"
                    >
                      <td class="font-weight-semibold">{{ t.display_name }}</td>
                      <td><code class="text-muted small">{{ t.source_endpoint }}</code></td>
                      <td>
                        <CBadge v-if="getOverrideCount(t.transformer_key) > 0" color="success">
                          {{ getOverrideCount(t.transformer_key) }} vínculo(s)
                        </CBadge>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-right"><fa-icon icon="chevron-right" class="text-muted" /></td>
                    </tr>
                    <tr v-if="filteredTransformers.length === 0">
                      <td colspan="4" class="text-center text-muted py-4">Nenhuma consulta encontrada</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </CCardBody>
          </CCard>
        </div>

        <!-- DETALHE de um transformer -->
        <div v-else>
          <div class="d-flex align-items-center flex-wrap mb-3" style="gap:8px">
            <CButton size="sm" color="secondary" variant="outline" @click="closeTransformer">
              <fa-icon icon="arrow-left" /> Voltar
            </CButton>
            <span class="font-weight-bold">{{ selectedTransformer.display_name }}</span>
            <code class="text-muted small">{{ selectedTransformer.source_endpoint }}</code>
            <CBadge color="primary" class="ml-auto">{{ currentOverrides.length }} vínculo(s)</CBadge>
          </div>

          <div v-if="loadingPreview" class="text-center py-4 text-muted">
            <fa-icon icon="spinner" spin /> Carregando campos...
          </div>

          <div v-if="!loadingPreview">

            <!-- Label da tabela destino -->
            <div v-if="selectedTransformer.target_model" class="mb-2 text-muted small">
              <fa-icon icon="database" class="text-primary mr-1" />
              Campos de <strong class="text-dark">{{ selectedTransformer.display_name.toLowerCase() }}</strong> da Versotech
              <code class="ml-1 bg-light text-muted px-1 rounded" style="font-size:11px">{{ targetModelLabel }}</code>
            </div>

            <!-- Filtros -->
            <div class="d-flex align-items-center flex-wrap mb-2" style="gap:8px">
              <input
                type="text"
                class="form-control form-control-sm"
                style="max-width:240px"
                v-model="versoSearch"
                placeholder="Buscar campo destino..."
                aria-label="Buscar campo destino"
              />
              <div class="d-flex" style="gap:4px">
                <CButton size="sm" :color="versoFilter === 'all' ? 'primary' : 'light'" @click="versoFilter = 'all'">
                  Todos <CBadge :color="versoFilter === 'all' ? 'light' : 'secondary'" class="ml-1">{{ versoColumns.length }}</CBadge>
                </CButton>
                <CButton size="sm" :color="versoFilter === 'pending' ? 'warning' : 'light'" @click="versoFilter = 'pending'">
                  Pendentes <CBadge :color="versoFilter === 'pending' ? 'dark' : 'secondary'" class="ml-1">{{ pendingCount }}</CBadge>
                </CButton>
                <CButton size="sm" :color="versoFilter === 'mapped' ? 'success' : 'light'" @click="versoFilter = 'mapped'">
                  Mapeados <CBadge :color="versoFilter === 'mapped' ? 'light' : 'secondary'" class="ml-1">{{ mappedCount }}</CBadge>
                </CButton>
              </div>
              <small class="text-muted ml-auto">
                {{ mappedCount }} de {{ versoColumns.length }} mapeados
                <span v-if="defaultMappedCount > 0" class="text-info"> · {{ defaultMappedCount }} padrão</span>
              </small>
            </div>

            <!-- Lista de campos Verso -->
            <CCard>
              <CCardBody class="p-0">
                <div style="max-height:calc(100vh - 310px);overflow-y:auto">
                  <template v-for="(cols, cat) in groupedVersoColumns">
                    <div
                      :key="cat + '__hdr'"
                      class="d-flex align-items-center px-3 py-2 bg-light border-bottom"
                      style="cursor:pointer;position:sticky;top:0;z-index:1"
                      @click="toggleCat(cat)"
                    >
                      <fa-icon :icon="collapsedCats[cat] ? 'chevron-right' : 'chevron-down'" class="text-muted mr-2 small" />
                      <span class="font-weight-semibold small text-uppercase text-muted">{{ cat }}</span>
                      <CBadge color="light" class="ml-2 text-muted">{{ cols.filter(c => c.override).length }}/{{ cols.length }}</CBadge>
                    </div>
                    <template v-if="!collapsedCats[cat]">
                      <div
                        v-for="col in cols"
                        :key="col.name"
                        class="d-flex align-items-center px-3 py-2 border-bottom cd-verso-row"
                        :class="{
                          'bg-success-subtle': col.override,
                          'bg-info-subtle': !col.override && col.default_mapped,
                        }"
                      >
                        <span class="font-monospace font-weight-semibold mr-2" style="min-width:180px;font-size:13px">
                          {{ col.name }}
                          <span v-if="col.required" class="text-danger ml-1" title="Campo obrigatório">*</span>
                        </span>

                        <!-- Override do usuário -->
                        <div v-if="col.override" class="d-flex align-items-center flex-wrap ml-auto" style="gap:6px">
                          <code class="text-primary small">{{ col.override.source_expression || '(fixo)' }}</code>
                          <CBadge :color="typeColor(col.override.transform_type)">{{ getTypeLabel(col.override.transform_type) }}</CBadge>
                          <CButton size="sm" color="primary" variant="outline" @click.stop="openMappingModal(col)" title="Editar vínculo">
                            <fa-icon icon="pencil-alt" />
                          </CButton>
                          <CButton size="sm" color="danger" variant="outline" @click.stop="deleteRule(col.override)" title="Excluir vínculo">
                            <fa-icon icon="trash-alt" />
                          </CButton>
                        </div>

                        <!-- Mapeamento padrão -->
                        <div v-else-if="col.default_mapped" class="d-flex align-items-center flex-wrap ml-auto" style="gap:6px">
                          <code v-if="col.default_source" class="text-info small">{{ col.default_source }}</code>
                          <span v-else class="text-muted small font-italic">(calculado)</span>
                          <CBadge color="info">padrão</CBadge>
                          <CButton size="sm" color="secondary" variant="outline" @click.stop="openMappingModal(col)" title="Personalizar mapeamento padrão">
                            <fa-icon icon="pencil-alt" /> Personalizar
                          </CButton>
                        </div>

                        <!-- Sem mapeamento -->
                        <div v-else class="ml-auto">
                          <CButton size="sm" color="primary" variant="ghost" @click.stop="openMappingModal(col)">
                            <fa-icon icon="plus" /> Vincular
                          </CButton>
                        </div>
                      </div>
                    </template>
                  </template>
                  <div v-if="Object.keys(groupedVersoColumns).length === 0" class="text-center text-muted py-4">
                    Nenhum campo encontrado
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </div>
        </div>
      </CTab>

      <!-- ═══ ABA 2: FILTROS ═══ -->
      <CTab title="Filtros de Consulta">
        <div class="d-flex align-items-center flex-wrap mb-3" style="gap:8px">
          <input
            type="text"
            class="form-control form-control-sm"
            style="max-width:280px"
            v-model="searchFilters"
            placeholder="Buscar endpoint ou filtro..."
            aria-label="Buscar filtro"
          />
          <small class="text-muted">{{ filters.length }} filtro(s) em {{ filtersEntityCount }} endpoint(s)</small>
        </div>

        <div v-if="!loading && transformerGroups.length === 0" class="text-center py-5 text-muted">
          <fa-icon icon="filter" class="d-block mb-2" style="font-size:2rem" />
          <div class="font-weight-semibold">Nenhum endpoint encontrado</div>
        </div>

        <CCard v-for="group in transformerGroups" :key="group.transformer.transformer_key" class="mb-3">
          <CCardHeader class="d-flex align-items-center" style="cursor:pointer" @click="toggleFilterGroup(group.transformer.transformer_key)">
            <fa-icon
              :icon="expandedFilterGroups[group.transformer.transformer_key] !== false ? 'chevron-down' : 'chevron-right'"
              class="text-muted mr-2 small"
            />
            <span class="font-weight-semibold">{{ group.transformer.display_name }}</span>
            <code class="text-muted small ml-2">{{ group.transformer.source_endpoint }}</code>
            <CBadge color="secondary" class="ml-2">{{ group.items.length }}</CBadge>
            <CButton
              size="sm"
              color="primary"
              variant="outline"
              class="ml-auto"
              @click.stop="openFilterForEntity(group.entityKey)"
            >
              <fa-icon icon="plus" /> Novo filtro
            </CButton>
          </CCardHeader>
          <CCardBody v-show="expandedFilterGroups[group.transformer.transformer_key] !== false" class="p-0">
            <div v-if="group.items.length === 0" class="p-3 text-muted small">
              Nenhum filtro configurado.
              <a class="text-primary" style="cursor:pointer" @click.stop="openFilterForEntity(group.entityKey)">Criar o primeiro →</a>
            </div>
            <div v-for="item in group.items" :key="item.id" class="d-flex align-items-center flex-wrap px-3 py-2 border-bottom" style="gap:8px">
              <code class="font-weight-bold">{{ item.filter_key }}</code>
              <CBadge :color="filterTypeColor(item.filter_type)">{{ item.filter_type }}</CBadge>
              <span class="text-muted small flex-grow-1 font-monospace" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ truncate(item.filter_value, 80) }}</span>
              <div class="d-flex" style="gap:4px">
                <CButton size="sm" color="primary" variant="outline" @click="$refs.filterModal.open(item)"><fa-icon icon="pencil-alt" /></CButton>
                <CButton size="sm" color="danger" variant="outline" @click="deleteFilter(item)"><fa-icon icon="trash-alt" /></CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CTab>

      <!-- ═══ ABA 3: DIMENSÕES / CATÁLOGOS ═══ -->
      <CTab title="Dimensões / Catálogos">
        <div class="d-flex align-items-center flex-wrap mb-3" style="gap:8px">
          <small class="text-muted">{{ dimensions.length }} dimensão(ões) configurada(s)</small>
          <CButton size="sm" color="primary" variant="outline" class="ml-auto" @click="openDimensionModal()">
            <fa-icon icon="plus" /> Nova dimensão
          </CButton>
        </div>

        <div v-if="!loading && dimensions.length === 0" class="text-center py-5 text-muted">
          <fa-icon icon="layer-group" class="d-block mb-2" style="font-size:2rem" />
          <div class="font-weight-semibold">Nenhuma dimensão configurada</div>
          <a class="text-primary" style="cursor:pointer" @click="openDimensionModal()">Criar a primeira →</a>
        </div>

        <CCard v-for="item in dimensions" :key="item.id" class="mb-2">
          <CCardBody class="d-flex align-items-center flex-wrap py-2" style="gap:10px">
            <CBadge color="primary" class="px-2">produto.dimensao{{ item.coluna }}</CBadge>
            <span class="font-weight-semibold">{{ item.dimensao_label || 'CATALOGO' }}</span>
            <code class="text-muted small">{{ item.source_endpoint }}</code>
            <span class="text-muted small font-monospace">
              <code class="text-primary">{{ item.id_field }}</code>
              <fa-icon icon="arrow-right" class="mx-1 text-muted" />
              <code class="text-success">{{ item.descricao_field }}</code>
            </span>
            <CBadge v-if="item.only_referenced" color="info" title="Carrega apenas valores referenciados pelos produtos">
              apenas referenciados
            </CBadge>
            <CBadge v-if="!item.is_active" color="secondary">inativo</CBadge>
            <div class="d-flex ml-auto" style="gap:4px">
              <CButton size="sm" color="primary" variant="outline" @click="openDimensionModal(item)" title="Editar">
                <fa-icon icon="pencil-alt" />
              </CButton>
              <CButton size="sm" color="danger" variant="outline" @click="deleteDimension(item)" title="Excluir">
                <fa-icon icon="trash-alt" />
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CTab>
    </CTabs>
  </div>
</template>

<script>
import { get, del } from "../../helpers/apiRequest";
import FilterModal from "./components/FilterModal";
import MappingRuleModal from "./components/MappingRuleModal";
import DimensionModal from "./components/DimensionModal";

function debounce(fn, delay) {
  let t;
  return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), delay); };
}

export default {
  name: "consulta-dinamica",
  components: { FilterModal, MappingRuleModal, DimensionModal },
  data() {
    return {
      activeTab: 0,
      loading: false,
      transformers: [],
      transformTypes: {},
      overrides: [],
      filters: [],
      dimensions: [],
      selectedTransformer: null,
      loadingPreview: false,
      previewData: null,
      versoColumns: [],
      versoSearch: "",
      versoFilter: "all",
      transformerSearch: "",
      searchFilters: "",
      expandedFilterGroups: {},
      filterChip: "all",
      collapsedCats: {},
    };
  },
  computed: {
    chipCounts() {
      return {
        all: this.transformers.length,
        mapped: this.transformers.filter(t => this.getOverrideCount(t.transformer_key) > 0).length,
      };
    },
    filteredTransformers() {
      return this.transformers.filter(t => {
        if (this.filterChip === "mapped" && this.getOverrideCount(t.transformer_key) === 0) return false;
        if (!this.transformerSearch) return true;
        const s = this.transformerSearch.toLowerCase();
        return t.display_name.toLowerCase().includes(s) || (t.source_endpoint || "").toLowerCase().includes(s);
      });
    },
    currentOverrides() {
      if (!this.selectedTransformer) return [];
      return this.overrides.filter(o => o.transformer_key === this.selectedTransformer.transformer_key);
    },
    // Memoizado para evitar recriar a cada render de filteredVersoColumns
    overrideMap() {
      const m = {};
      this.currentOverrides.forEach(o => { m[o.verso_field] = o; });
      return m;
    },
    targetModelLabel() {
      if (!this.selectedTransformer || !this.selectedTransformer.target_model) return "";
      const className = this.selectedTransformer.target_model.split("\\").pop();
      return className.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
    },
    filteredVersoColumns() {
      const s = this.versoSearch.toLowerCase();
      return this.versoColumns
        .map(col => {
          const name = typeof col === "string" ? col : col.name;
          const info = typeof col === "object" ? col : {};
          return {
            name,
            category: info.category || this.categorize(name),
            required: info.required || false,
            hint: info.hint || "",
            default_mapped: !!(info.default_mapped && info.default_source),
            default_source: info.default_source || null,
            default_type: info.default_type || null,
            override: this.overrideMap[name] || null,
          };
        })
        .filter(c => {
          if (this.versoFilter === "pending" && (c.override || c.default_mapped)) return false;
          if (this.versoFilter === "mapped" && !c.override && !c.default_mapped) return false;
          if (!s) return true;
          return c.name.toLowerCase().includes(s) ||
            (c.override && c.override.source_expression && c.override.source_expression.toLowerCase().includes(s));
        });
    },
    groupedVersoColumns() {
      const cats = {};
      this.filteredVersoColumns.forEach(col => {
        const cat = col.category || "Outros";
        if (!cats[cat]) cats[cat] = [];
        cats[cat].push(col);
      });
      const order = ["Identificação", "Endereço", "Contato", "Comercial"];
      const sorted = {};
      order.forEach(k => { if (cats[k]) sorted[k] = cats[k]; });
      Object.keys(cats).sort().forEach(k => { if (!sorted[k]) sorted[k] = cats[k]; });
      return sorted;
    },
    mappedCount() {
      return this.filteredVersoColumns.filter(c => c.override || c.default_mapped).length;
    },
    defaultMappedCount() {
      return this.filteredVersoColumns.filter(c => c.default_mapped && !c.override).length;
    },
    pendingCount() {
      return this.versoColumns.filter(col => {
        const name = typeof col === "string" ? col : col.name;
        return !this.overrideMap[name] && !(typeof col === "object" && col.default_mapped);
      }).length;
    },
    transformerGroups() {
      const s = this.searchFilters.toLowerCase();
      return this.transformers.map(t => {
        const entityKey = t.entity_type || t.transformer_key;
        const items = this.filters.filter(f => f.entity === entityKey || f.entity === t.transformer_key);
        const visible = !s ||
          t.display_name.toLowerCase().includes(s) ||
          (t.source_endpoint || "").toLowerCase().includes(s) ||
          items.some(f => (f.filter_key || "").toLowerCase().includes(s) || (f.filter_value || "").toLowerCase().includes(s));
        return { transformer: t, entityKey, items, visible };
      }).filter(g => !s || g.visible);
    },
    filtersEntityCount() {
      return new Set(this.filters.map(f => f.entity)).size;
    },
  },
  methods: {
    onTabChange(idx) { this.activeTab = idx; },
    truncate(v, max) { return v && v.length > max ? v.substring(0, max) + "..." : v; },
    getTypeLabel(type) { return this.transformTypes[type] ? this.transformTypes[type].label : type; },
    getOverrideCount(key) { return this.overrides.filter(o => o.transformer_key === key).length; },
    typeColor(type) {
      const map = { direct: "success", fixed: "secondary", map: "warning", conditional: "info", first_match: "info", fallback: "primary", coalesce: "primary", concat: "dark", format: "info", default: "light" };
      return map[type] || "secondary";
    },
    filterTypeColor(type) {
      const map = { odata: "primary", parameter: "warning", sql: "info", post_filter: "secondary", source: "success" };
      return map[type] || "light";
    },
    categorize(name) {
      const n = (name || "").toLowerCase();
      if (/^(cep|logradouro|endereco|numero|complemento|bairro|cidade|uf|estado|pais)$/.test(n)) return "Endereço";
      if (/(email|telefone|celular|phone|site|website)/.test(n)) return "Contato";
      if (/(representante|supervisor|vendedor|tipo_cli|comercial|moeda|currency|limite|credito)/.test(n)) return "Comercial";
      return "Identificação";
    },
    toggleCat(cat) { this.$set(this.collapsedCats, cat, !this.collapsedCats[cat]); },
    toggleFilterGroup(key) {
      // Bug fix: alterna corretamente entre aberto (undefined/true) e fechado (false)
      this.$set(this.expandedFilterGroups, key, this.expandedFilterGroups[key] !== false ? false : true);
    },
    openTransformer(t) {
      this.selectedTransformer = t;
      this.versoColumns = [];
      this.previewData = null;
      this.versoFilter = "all";
      this.versoSearch = "";
      this.collapsedCats = {};
      this.loadPreview();
      this.loadVersoColumns();
    },
    closeTransformer() {
      this.selectedTransformer = null;
      this.versoSearch = "";
      this.versoFilter = "all";
      this.collapsedCats = {};
    },
    loadPreview() {
      this.loadingPreview = true;
      get(`/admin/erp-transformer-preview/${this.selectedTransformer.transformer_key}`)
        .then(res => { this.previewData = res.raw_data || {}; })
        .catch(() => { this.previewData = {}; })
        .finally(() => { this.loadingPreview = false; });
    },
    loadVersoColumns() {
      get(`/admin/erp-transformer-fields/${this.selectedTransformer.transformer_key}`)
        .then(res => {
          const cols = res.target_model_columns || {};
          this.versoColumns = Object.keys(cols).map(name => {
            const info = (cols[name] && typeof cols[name] === "object") ? cols[name] : {};
            return {
              name,
              category: info.category || this.categorize(name),
              required: info.required || (info.nullable === false) || false,
              hint: info.hint || info.comment || "",
              default_mapped: !!(info.mapped && info.default_source),
              default_source: info.default_source || null,
              default_type: info.default_type || null,
            };
          });
        })
        .catch(() => {
          this.versoColumns = [];
          this.$swal && this.$swal({ title: "Erro", text: "Não foi possível carregar os campos destino.", icon: "warning" });
        });
    },
    openMappingModal(col) {
      if (col && col.override) {
        this.$refs.mappingModal.open(col.override);
      } else {
        const initial = { transformer_key: this.selectedTransformer.transformer_key };
        if (col) initial.verso_field = col.name;
        this.$refs.mappingModal.open(initial);
      }
    },
    openFilterForEntity(entityKey) {
      this.$refs.filterModal.open({ entity: entityKey });
    },
    deleteRule(item) {
      this.$swal({
        title: "Excluir vínculo?",
        text: `${item.source_expression || "(fixo)"} → ${item.verso_field}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
      }).then(r => {
        if (r.value) {
          del(`/admin/erp-field-overrides/${item.id}`)
            .then(() => this.loadOverrides())
            .catch(() => this.$swal && this.$swal({ title: "Erro ao excluir", icon: "error" }));
        }
      });
    },
    deleteFilter(item) {
      this.$swal({
        title: "Excluir filtro?",
        text: item.filter_key,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
      }).then(r => {
        if (r.value) {
          del(`/admin/erp-filter-configs/${item.id}`)
            .then(() => this.loadFilters())
            .catch(() => this.$swal && this.$swal({ title: "Erro ao excluir", icon: "error" }));
        }
      });
    },
    loadOverrides() {
      get("/admin/erp-field-overrides?take=200")
        .then(res => { this.overrides = res.itens || res.data || res; })
        .catch(() => {});
    },
    loadFilters() {
      get("/admin/erp-filter-configs?take=200")
        .then(res => { this.filters = res.itens || res.data || res; })
        .catch(() => {});
    },
    loadDimensions() {
      get("/admin/erp-dimension-configs?take=200")
        .then(res => { this.dimensions = res.itens || res.data || res; })
        .catch(() => {});
    },
    openDimensionModal(item) {
      this.$refs.dimensionModal.open(item || null);
    },
    deleteDimension(item) {
      this.$swal({
        title: "Excluir dimensão?",
        text: `produto.dimensao${item.coluna} · ${item.source_endpoint}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
      }).then(r => {
        if (r.value) {
          del(`/admin/erp-dimension-configs/${item.id}`)
            .then(() => this.loadDimensions())
            .catch(() => this.$swal && this.$swal({ title: "Erro ao excluir", icon: "error" }));
        }
      });
    },
  },
  created() {
    // Inicializa debounce separado para evitar reatividade circular
    this.loading = true;
    Promise.all([
      get("/admin/erp-transformers?direction=inbound").then(res => { this.transformers = res; }),
      get("/admin/erp-transform-types").then(res => { this.transformTypes = res; }),
      get("/admin/erp-field-overrides?take=200").then(res => { this.overrides = res.itens || res.data || res; }),
      get("/admin/erp-filter-configs?take=200").then(res => { this.filters = res.itens || res.data || res; }),
      get("/admin/erp-dimension-configs?take=200").then(res => { this.dimensions = res.itens || res.data || res; }),
    ])
      .catch(() => { this.$swal && this.$swal({ title: "Erro ao carregar", text: "Verifique a conexão com a API.", icon: "error" }); })
      .finally(() => { this.loading = false; });
  },
};
</script>

<style scoped>
.cd-verso-row { transition: background 0.1s; }
.cd-verso-row:hover { background: #f8f9fa !important; }
.bg-success-subtle { background: #d4edda !important; }
.bg-info-subtle { background: #d1ecf1 !important; }
.font-monospace { font-family: 'SF Mono', Consolas, monospace !important; }
.font-weight-semibold { font-weight: 600 !important; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
</style>
